import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, X } from 'lucide-react';
import { apiGet, formatPrice, seatViewUrl, type Game, type Listing } from '../lib/api';
import { describeSection } from '../lib/metlife';
import { demoListingsFor, isDemoListing, useDemoMode } from '../lib/demo';
import { SectionZoom } from './StadiumMap';

/**
 * Section drill-down panel (right column of the stadium-map page): where the
 * section sits, availability summary, and the listings themselves.
 */
export default function SectionPanel({
  section,
  querySection,
  gameId,
  game,
  onClose,
}: {
  /** Map cell id, e.g. "111A" — used for display + zoom. */
  section: string;
  /** Section string as stored in D1 (usually the same) — used for the query. */
  querySection?: string;
  gameId: string;
  game?: Game;
  onClose: () => void;
}) {
  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);
  const [demoOn] = useDemoMode();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiGet<{ listings: Listing[] }>(
      `/listings?section=${encodeURIComponent(querySection ?? section)}&game_id=${gameId}&sort=price_asc`
    )
      .then(({ listings }) => !cancelled && setListings(listings))
      .catch(() => !cancelled && setListings([]))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [section, querySection, gameId]);

  const all = useMemo(() => {
    const demo = demoOn ? demoListingsFor(gameId, section, game) : [];
    return [...listings, ...demo].sort(
      (a, b) => a.price_per_seat_cents - b.price_per_seat_cents
    );
  }, [listings, demoOn, gameId, section, game]);

  const sectionType = all[0]?.section_type;

  return (
    <div className="card rounded-xl p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="!mb-0.5">Section {section}</h3>
          <p className="text-sm muted m-0">{describeSection(section)}</p>
        </div>
        <button
          onClick={onClose}
          aria-label="Close section details"
          className="bg-transparent hover:bg-surface-100 !text-ink-600 p-1.5 rounded-md"
        >
          <X size={16} />
        </button>
      </div>

      <div className="flex gap-2 flex-wrap mt-3">
        {sectionType && <span className="badge">{sectionType}</span>}
        <span className="badge">
          {all.length} listing{all.length === 1 ? '' : 's'}
        </span>
      </div>

      <div className="mt-4">
        <SectionZoom sectionId={section} />
        <p className="muted text-xs mt-1.5 mb-0">
          Section {section} highlighted · Giants sideline at the top.
        </p>
      </div>

      <div className="flex flex-col gap-3 mt-4">
        {loading && <p className="muted m-0">Loading listings…</p>}
        {!loading && all.length === 0 && (
          <p className="muted m-0">No listings in this section for this game.</p>
        )}
        {all.map((l) => {
          const seats: string[] = JSON.parse(l.seats_json);
          const sample = isDemoListing(l.listing_id);
          return (
            <div key={l.listing_id} className="border border-ink-300 rounded-lg p-3 hover:border-giants-blue transition-colors">
              {l.seat_view_photo_url && (
                <img
                  src={seatViewUrl(l.seat_block_id)}
                  alt={`View from section ${l.section}`}
                  loading="lazy"
                  className="w-full aspect-video object-cover rounded-md mb-2"
                />
              )}
              <div className="flex justify-between items-start gap-2 mb-1">
                <span className="text-sm font-bold text-ink-900">
                  Row {l.row} · Seats {seats.join(', ')}
                </span>
                <span className="text-base font-bold text-giants-blue tabular-nums shrink-0">
                  {formatPrice(l.price_per_seat_cents)}
                </span>
              </div>
              {l.listing_note && <p className="text-xs muted mt-0 mb-1.5">"{l.listing_note}"</p>}
              <div className="flex items-center justify-between gap-2">
                <span className="flex gap-1.5 flex-wrap">
                  {sample ? (
                    <span className="badge sample">Sample</span>
                  ) : (
                    <span className="badge verified">
                      <CheckCircle size={10} /> Verified
                    </span>
                  )}
                  <span className="text-xs muted self-center">
                    {l.quantity_available - l.quantity_sold} left
                  </span>
                </span>
                {sample ? (
                  <span
                    className="btn !py-1 !px-3 text-xs opacity-50 cursor-not-allowed"
                    title="Sample listing — turn off demo data to hide"
                  >
                    Sample
                  </span>
                ) : (
                  <Link to={`/checkout/${l.listing_id}`} className="btn !py-1 !px-3 text-xs">
                    Buy now
                  </Link>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
